// src/components/Chat/ChatInput.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStop, faArrowUp, faColumns } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from 'react-hot-toast';

import FileUpload from '../FileUpload';
import FileProcessingStatus from '../FileProcessingStatus';

const ChatInput = ({ 
  chatActions, 
  attachedFiles,
  setAttachedFiles,
  message,
  setMessage,
  currentChat, 
  templates, 
  apiModels,
  hasActiveFileProcessingJobs,
  onCancelAllFileProcessing,
  onUpdateChat
}) => {
  const containerRef = useRef(null);
  // State для отслеживания фокуса селекта (mobile)
  const [selectFocused, setSelectFocused] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedApiModel, setSelectedApiModel] = useState('');
  const textareaRef = useRef(null);
  
  // При фокусе на селекте на мобильных переключаем класс и скроллим сам селект
  const handleSelectFocus = (e) => {
    setSelectFocused(true);
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };
  const handleSelectBlur = () => {
    setSelectFocused(false);
  };

  // Отображаем только активные или недавно завершенные задачи
  const activeJobs = chatActions.jobs
    .filter(job => job.status === 'active' || job.status === 'queued' || job.status === 'accepted')
    .map(job => ({
        id: job.jobId,
        jobId: job.jobId,
        name: job.fileName,
        size: job.fileSize,
        status: job.status,
        progress: job.progress,
        stage: job.stage,
        error: job.error,
    }));

  // --- НАЧАЛО КЛЮЧЕВОГО ИСПРАВЛЕНИЯ ---
  // Синхронизируем селекторы только при СМЕНЕ ЧАТА, а не при каждом обновлении.
  useEffect(() => {
    if (currentChat) {
      // Приоритет: chatActions.selectedTemplateId (из URL) > currentChat.prompt._id (из базы)
      const templateId = chatActions.selectedTemplateId || currentChat.prompt?._id || '';
      setSelectedTemplate(templateId);
      if (chatActions.selectedTemplateId && chatActions.selectedTemplateId !== currentChat.prompt?._id) {
        // Если шаблон выбран из URL, обновляем чат
        updateChatMetadata({ promptId: chatActions.selectedTemplateId });
      }
      const apiModelId = currentChat.apiModel?._id || (apiModels.length > 0 ? apiModels[0]._id : '');
      setSelectedApiModel(apiModelId);
      
      // Если у чата нет API модели, но есть доступные модели - обновляем чат
      if (!currentChat.apiModel && apiModelId && apiModels.length > 0) {
        updateChatMetadata({ apiModelId });
      }
    }
  }, [currentChat?._id, apiModels, chatActions.selectedTemplateId]); // Зависимость от ID чата, а не от всего объекта
  // --- КОНЕЦ КЛЮЧЕВОГО ИСПРАВЛЕНИЯ ---

  const handleFileAttach = (newFiles) => {
    const filesToAdd = [];
    for (const newFile of newFiles) {
        const isDuplicate = attachedFiles.some(
            existingFile => existingFile.name === newFile.name && existingFile.size === newFile.size
        );
        if (!isDuplicate) {
            filesToAdd.push(newFile);
        } else {
            toast.error(`Файл ${newFile.name} уже прикреплен.`);
        }
    }
    if (filesToAdd.length > 0) {
        setAttachedFiles(prev => [...prev, ...filesToAdd]);
    }
  };

  const updateChatMetadata = async (data) => {
    if (!currentChat) return;
    try {
      const res = await axios.put(`/api/chat/${currentChat._id}`, data);
      onUpdateChat(res.data);
      toast.success('Настройки чата обновлены', { duration: 1500 });
    } catch (err) {
      console.error('Ошибка при обновлении настроек чата:', err);
      toast.error('Не удалось обновить настройки');
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    chatActions.setSelectedTemplateId(templateId);
    updateChatMetadata({ promptId: templateId });
  };

  const handleApiModelChange = (modelId) => {
    setSelectedApiModel(modelId);
    updateChatMetadata({ apiModelId: modelId });
  };
  
  const handleModelSelectionForComparison = (modelIndex, modelId) => {
    const newSelection = [...chatActions.selectedModelsForComparison];
    newSelection[modelIndex] = modelId;
    chatActions.setSelectedModelsForComparison(newSelection);
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (chatActions.isDualComparisonMode) {
      // DUAL РЕЖИМ: проверяем что выбраны 2 модели
      if (chatActions.selectedModelsForComparison.length < 2) {
        toast.error('Для сравнения нужно выбрать 2 модели');
        return;
      }
      chatActions.sendDualStreamingMessage(message, selectedTemplate);
    } else {
      // ОДИНОЧНЫЙ РЕЖИМ: проверяем что выбрана API модель
    const apiModelToUse = selectedApiModel || (apiModels.length > 0 ? apiModels[0]._id : '');
    if (!apiModelToUse) {
      toast.error('Не выбрана API модель');
      return;
    }
    chatActions.sendMessage(message, selectedTemplate, apiModelToUse);
    }
    
    resetTextareaHeight();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleFormSubmit(e);
    }
  };
  
  const isStreaming = chatActions.streamingChat.isStreaming;
  const isDualStreaming = chatActions.dualStreaming.isDualStreaming;
  const isLoading = chatActions.loading;

  return (
    <div className={`chat-input${selectFocused ? ' select-focused' : ''}`}>
      <div className="chat-input-container" ref={containerRef}>
        {chatActions.uploadError && <div className="alert alert-danger py-2 mb-2">{chatActions.uploadError}</div>}
        {chatActions.streamingChat.streamingError && <div className="alert alert-danger py-2 mb-2">Ошибка потоковой передачи: {chatActions.streamingChat.streamingError}</div>}
        {chatActions.dualStreaming.dualStreamingError && <div className="alert alert-danger py-2 mb-2">Ошибка сравнения моделей: {chatActions.dualStreaming.dualStreamingError}</div>}
        
        {/* === Статус обработки файлов (возвращаем к исходной логике) === */}
        <FileProcessingStatus 
          processingFiles={chatActions.processingFiles.filter(f => !f.chatId || f.chatId === currentChat?._id)}
          onRemoveFile={(fileId) => {
            chatActions.cancelLocalUpload(fileId);
          }}
        />
        
        {/* === Блок прикрепленных файлов (ПЕРЕМЕЩЕН НАВЕРХ) === */}
        {attachedFiles.length > 0 && (
          <div className="attached-files-preview-container">
            {attachedFiles.map((fileData) => (
              <div key={fileData.id} className="attached-file-preview">
                <span className="file-icon">{chatActions.fileUploadRef.current?.getFileIcon(fileData.type) || '📎'}</span>
                <div className="file-details">
                    <span className="file-name">{fileData.name}</span>
                    <span className="file-size">{chatActions.fileUploadRef.current?.formatFileSize(fileData.size) || ''}</span>
                </div>
                <button 
                  type="button" 
                  className="remove-file-btn"
                  onClick={() => {
                      const newFiles = attachedFiles.filter(f => f.id !== fileData.id);
                      setAttachedFiles(newFiles);
                  }}
                  title="Открепить файл"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        
        <form onSubmit={handleFormSubmit}>
          <div className="enhanced-input-group">
            <textarea
              ref={textareaRef}
              className="form-control enhanced-textarea"
              placeholder="Введите сообщение..."
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 300)}px`;
              }}
              onKeyDown={handleKeyDown}
              disabled={isLoading || isStreaming || isDualStreaming}
              name="messageInput"
            />
            
            <div className="input-footer">
              <div className="input-left-controls">
                <FileUpload 
                  ref={chatActions.fileUploadRef}
                  onFileAttach={handleFileAttach}
                  onError={chatActions.handleUploadError}
                />
                <select
                  className="form-select form-select-sm template-select"
                  value={selectedTemplate}
                  onFocus={handleSelectFocus}
                  onBlur={handleSelectBlur}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  title="Выбрать шаблон"
                >
                  <option value="">Без шаблона</option>
                  {templates.map(template => (
                    <option key={template._id} value={template._id}>{template.title}</option>
                  ))}
                </select>
              </div>
              
              <div className="char-counter">
                {message.length > 0 && (
                  <span>{message.length.toLocaleString()} символов</span>
                )}
              </div>
              
              <div className="input-right-controls">
                <button
                  type="button"
                  className={`btn btn-sm me-2 dual-toggle ${chatActions.isDualComparisonMode ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => chatActions.setIsDualComparisonMode(prev => !prev)}
                  disabled={apiModels.length < 2 || isStreaming || isDualStreaming}
                  title={chatActions.isDualComparisonMode ? 'Отключить сравнение моделей' : 'Сравнить 2 модели'}
                >
                  <FontAwesomeIcon icon={faColumns} />
                  {chatActions.isDualComparisonMode && <span className="ms-1">VS</span>}
                </button>

                {chatActions.isDualComparisonMode ? (
                  <div className="dual-model-selects d-flex gap-1">
                    <select
                      className="form-select form-select-sm model-select"
                      value={chatActions.selectedModelsForComparison[0] || ''}
                      onFocus={handleSelectFocus}
                      onBlur={handleSelectBlur}
                      onChange={(e) => handleModelSelectionForComparison(0, e.target.value)}
                    >
                      <option value="" disabled>Модель 1</option>
                      {apiModels.map(model => <option key={model._id} value={model._id}>{model.name}</option>)}
                    </select>
                    <select
                      className="form-select form-select-sm model-select"
                      value={chatActions.selectedModelsForComparison[1] || ''}
                      onFocus={handleSelectFocus}
                      onBlur={handleSelectBlur}
                      onChange={(e) => handleModelSelectionForComparison(1, e.target.value)}
                    >
                      <option value="" disabled>Модель 2</option>
                      {apiModels.map(model => <option key={model._id} value={model._id}>{model.name}</option>)}
                    </select>
                  </div>
                ) : (
                  <select
                    className="form-select form-select-sm model-select"
                    value={selectedApiModel}
                    onFocus={handleSelectFocus}
                    onBlur={handleSelectBlur}
                    onChange={(e) => handleApiModelChange(e.target.value)}
                    title="Выбрать модель"
                  >
                    <option value="" disabled={apiModels.length > 0}>Выберите модель</option>
                    {apiModels.map(model => <option key={model._id} value={model._id}>{model.name}</option>)}
                  </select>
                )}
                
                {(isStreaming || isDualStreaming || hasActiveFileProcessingJobs) ? (
                  <button
                    className="stop-button"
                    type="button"
                    onClick={hasActiveFileProcessingJobs ? onCancelAllFileProcessing : (isDualStreaming ? chatActions.handleStopDualStreaming : chatActions.handleStopStreaming)}
                    title={hasActiveFileProcessingJobs ? "Отменить обработку файлов" : "Остановить генерацию"}
                  >
                    <FontAwesomeIcon icon={faStop} />
                  </button>
                ) : (
                  <button
                    className="send-button"
                    type="submit"
                    disabled={isLoading || (!message.trim() && attachedFiles.length === 0) || (chatActions.isDualComparisonMode && chatActions.selectedModelsForComparison.length < 2)}
                    title="Отправить сообщение"
                  >
                    {isLoading ? <span className="spinner-border spinner-border-sm" role="status"></span> : <FontAwesomeIcon icon={faArrowUp} />}
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatInput;